import { NUCLEOTIDE_CODES } from "@/lib/tool-kit/biology-genetics/iupac";

/**
 * The standard genetic code — NCBI translation table 1
 * (https://www.ncbi.nlm.nih.gov/Taxonomy/Utils/wprintgc.cgi), written the way NCBI
 * writes it: three base strings read in parallel, codons in T, C, A, G order.
 */
const TABLE_1_AAS =
  "FFLLSSSSYY**CC*WLLLLPPPPHHQQRRRRIIIMTTTTNNKKSSRRVVVVAAAADDEEGGGG";
const TABLE_1_STARTS =
  "---M------**--*----M---------------M----------------------------";
const BASE_1 =
  "TTTTTTTTTTTTTTTTCCCCCCCCCCCCCCCCAAAAAAAAAAAAAAAAGGGGGGGGGGGGGGGG";
const BASE_2 =
  "TTTTCCCCAAAAGGGGTTTTCCCCAAAAGGGGTTTTCCCCAAAAGGGGTTTTCCCCAAAAGGGG";
const BASE_3 =
  "TCAGTCAGTCAGTCAGTCAGTCAGTCAGTCAGTCAGTCAGTCAGTCAGTCAGTCAGTCAGTCAG";

function buildTable(): Record<string, string> {
  const table: Record<string, string> = {};
  for (let i = 0; i < TABLE_1_AAS.length; i++) {
    table[`${BASE_1[i]}${BASE_2[i]}${BASE_3[i]}`] = TABLE_1_AAS[i];
  }
  return table;
}

/** Codon (DNA letters) → one-letter amino acid, "*" for a stop. All 64 codons. */
export const STANDARD_CODE: Readonly<Record<string, string>> = buildTable();

/** Codons table 1 allows as initiators: TTG, CTG and ATG (ATG is the usual one). */
export const START_CODONS: readonly string[] = [...TABLE_1_STARTS].flatMap(
  (mark, i) => (mark === "M" ? [`${BASE_1[i]}${BASE_2[i]}${BASE_3[i]}`] : [])
);

export const STOP_CODONS: readonly string[] = Object.entries(STANDARD_CODE)
  .filter(([, aa]) => aa === "*")
  .map(([codon]) => codon);

export interface AminoAcidInfo {
  one: string;
  three: string;
  name: string;
}

/** The twenty standard amino acids in alphabetical order of one-letter code. */
export const AMINO_ACIDS: readonly AminoAcidInfo[] = [
  { one: "A", three: "Ala", name: "Alanine" },
  { one: "C", three: "Cys", name: "Cysteine" },
  { one: "D", three: "Asp", name: "Aspartic acid" },
  { one: "E", three: "Glu", name: "Glutamic acid" },
  { one: "F", three: "Phe", name: "Phenylalanine" },
  { one: "G", three: "Gly", name: "Glycine" },
  { one: "H", three: "His", name: "Histidine" },
  { one: "I", three: "Ile", name: "Isoleucine" },
  { one: "K", three: "Lys", name: "Lysine" },
  { one: "L", three: "Leu", name: "Leucine" },
  { one: "M", three: "Met", name: "Methionine" },
  { one: "N", three: "Asn", name: "Asparagine" },
  { one: "P", three: "Pro", name: "Proline" },
  { one: "Q", three: "Gln", name: "Glutamine" },
  { one: "R", three: "Arg", name: "Arginine" },
  { one: "S", three: "Ser", name: "Serine" },
  { one: "T", three: "Thr", name: "Threonine" },
  { one: "V", three: "Val", name: "Valine" },
  { one: "W", three: "Trp", name: "Tryptophan" },
  { one: "Y", three: "Tyr", name: "Tyrosine" },
];

const THREE_LETTER: Readonly<Record<string, string>> = {
  ...Object.fromEntries(AMINO_ACIDS.map((aa) => [aa.one, aa.three])),
  "*": "Ter",
  X: "Xaa",
  B: "Asx",
  Z: "Glx",
  J: "Xle",
  U: "Sec",
  O: "Pyl",
};

export function threeLetter(oneLetter: string): string {
  return THREE_LETTER[oneLetter] ?? "Xaa";
}

export function toThreeLetter(protein: string, separator = "-"): string {
  return [...protein].map(threeLetter).join(separator);
}

const ambiguousCache = new Map<string, string>();

/** Both Asx-type mixes that IUPAC gives their own letter: B = N/D, Z = Q/E, J = L/I. */
function mergeAminoAcids(found: ReadonlySet<string>): string {
  if (found.size === 1) {
    return [...found][0];
  }
  const key = [...found].sort().join("");
  if (key === "DN") {
    return "B";
  }
  if (key === "EQ") {
    return "Z";
  }
  if (key === "IL") {
    return "J";
  }
  return "X";
}

function translateAmbiguous(codon: string): string {
  const cached = ambiguousCache.get(codon);
  if (cached !== undefined) {
    return cached;
  }
  const options = [...codon].map((base) => NUCLEOTIDE_CODES[base] ?? "");
  if (options.some((choices) => choices === "")) {
    ambiguousCache.set(codon, "X");
    return "X";
  }
  const found = new Set<string>();
  for (const a of options[0]) {
    for (const b of options[1]) {
      for (const c of options[2]) {
        found.add(STANDARD_CODE[`${a}${b}${c}`]);
      }
    }
  }
  const result = mergeAminoAcids(found);
  ambiguousCache.set(codon, result);
  return result;
}

/**
 * One codon (DNA letters, upper case) to its amino acid. A codon with ambiguity codes
 * gets an amino acid only when every possible codon agrees (GCN is always Ala); a mix
 * of Asn/Asp is B, Gln/Glu is Z, Leu/Ile is J, and any other mix is X. A mix that
 * includes a stop codon is X.
 */
export function translateCodon(codon: string): string {
  return STANDARD_CODE[codon] ?? translateAmbiguous(codon);
}

export interface TranslateOptions {
  /** Stop at the first stop codon (it is not included in the protein). */
  toStop?: boolean;
  /** Begin at the first ATG in the frame, translating it as Met. */
  fromFirstStart?: boolean;
}

export interface Translation {
  /** One letter per codon; "*" marks a stop codon. */
  protein: string;
  /** 0-based position of the first translated base. */
  startIndex: number;
  /** 0-based position just after the last translated base. */
  endIndex: number;
  /** Bases left over after the last complete codon (not translated). */
  leftover: number;
}

/** DNA letters only: callers convert U to T first. `offset` is 0, 1 or 2. */
export function translateFrame(
  sequence: string,
  offset: number,
  options: TranslateOptions = {}
): Translation {
  let begin = offset;
  if (options.fromFirstStart) {
    while (
      begin + 3 <= sequence.length &&
      sequence.slice(begin, begin + 3) !== "ATG"
    ) {
      begin += 3;
    }
    if (begin + 3 > sequence.length) {
      return {
        protein: "",
        startIndex: sequence.length,
        endIndex: sequence.length,
        leftover: 0,
      };
    }
  }
  const residues: string[] = [];
  let i = begin;
  for (; i + 3 <= sequence.length; i += 3) {
    const aa = translateCodon(sequence.slice(i, i + 3));
    if (aa === "*" && options.toStop) {
      break;
    }
    residues.push(aa);
  }
  const stoppedEarly = i + 3 <= sequence.length;
  return {
    protein: residues.join(""),
    startIndex: begin,
    endIndex: i,
    leftover: stoppedEarly ? 0 : sequence.length - i,
  };
}
